import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { moneda, fechaCorta } from "./pagosCxpPDF";
import {
  datosEntradaSinTallas,
  dibujaEntradaSinTallas,
  piePaginas,
  nombreEmpresaPDF,
} from "../NotasCxP/entradaSinTallasPDF";

// (2026-10) PDF del contrato (salida a proceso) de Pagos y Saldos: la primera
// hoja es el contrato con sus entregas, anticipos y lo que falta; después va
// cada entrega en su propia hoja (el mismo PDF de la entrada sin tallas).

const AZUL = [30, 58, 138];
const AZUL_CLARO = [37, 99, 235];
const FONDO = [238, 244, 255];
const BORDE = [169, 180, 200];
const ROJO = [220, 38, 38];
const ML = 17;

const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const dato = (v) => {
  const t = String(v ?? "").trim();
  return !t || t.toUpperCase() === "NA" ? "" : t;
};
const etiqueta = (content) => ({
  content,
  styles: { fillColor: FONDO, textColor: AZUL, fontStyle: "bold" },
});
const derecha = (content, extra = {}) => ({ content, styles: { halign: "right", ...extra } });

export async function contratoPDF(idSalida) {
  try {
    const { data: c } = await axios.get(`${process.env.REACT_APP_URL_CXP_PAGOS}Contrato/${idSalida}`, {
      headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
    });
    // cada entrega con nota va en su hoja; las que no tienen nota sólo salen en la lista
    const entregas = (
      await Promise.all(
        c.entregas.filter((e) => e.nota?._id).map((e) => datosEntradaSinTallas(e.nota._id).catch(() => null))
      )
    ).filter(Boolean);

    const doc = new jsPDF();
    const empresa = nombreEmpresaPDF(c.contrato.empresa);
    dibujaContrato(doc, c, empresa);
    const secciones = [
      { desde: 1, izquierda: `${empresa.toUpperCase()} - Contrato de Proceso`, centro: `Contrato: ${c.contrato.folio}` },
    ];
    entregas.forEach((d) => {
      doc.addPage();
      secciones.push({
        desde: doc.internal.getNumberOfPages(),
        izquierda: `${nombreEmpresaPDF(d.empresa).toUpperCase()} - Entrada de Proceso`,
        centro: `Contrato ${c.contrato.folio} · Entrada ${d.entrada?.idProcesosEntrada ?? ""}`,
      });
      dibujaEntradaSinTallas(doc, d);
    });
    piePaginas(doc, secciones);
    doc.save(`Contrato ${c.contrato.folio} - ${c.taller.nombre}.pdf`);
  } catch (err) {
    console.error(err);
    Swal.fire("Error", err.response?.data?.message || "No se pudo generar el PDF del contrato", "error");
  }
}

function dibujaContrato(doc, { contrato, taller, articulos, entregas, anticipos, resumen }, empresa) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const anchoUtil = pageW - ML * 2;
  const tabla = (opciones) =>
    doc.autoTable({
      margin: { left: ML, right: ML, bottom: 22 },
      tableWidth: anchoUtil,
      theme: "grid",
      styles: { fontSize: 9.5, cellPadding: 2, lineColor: BORDE, lineWidth: 0.25, textColor: [30, 30, 30] },
      headStyles: { fillColor: AZUL, textColor: 255, fontStyle: "bold", halign: "center" },
      rowPageBreak: "avoid",
      ...opciones,
    });
  const titulo = (texto) => {
    let y = doc.autoTable.previous.finalY + 7;
    if (y > pageH - 45) {
      doc.addPage();
      y = 20;
    }
    doc.setFont(undefined, "bold").setFontSize(10.5).setTextColor(...AZUL);
    doc.text(texto, ML, y);
    doc.setTextColor(0).setFont(undefined, "normal");
    return y + 2;
  };

  // ---- encabezado
  doc.setFont(undefined, "bold").setFontSize(17).setTextColor(...AZUL);
  doc.text(empresa.toUpperCase(), ML, 20);
  doc.setFontSize(11.5).setTextColor(...AZUL_CLARO);
  doc.text("CONTRATO DE PROCESO", ML, 26.5);
  doc.setFontSize(10.5).setTextColor(...AZUL);
  doc.text("CONTRATO", pageW - ML, 19, { align: "right" });
  doc.setFontSize(16).setTextColor(...ROJO);
  doc.text(String(contrato.folio ?? ""), pageW - ML, 26.5, { align: "right" });
  doc.setTextColor(0).setFont(undefined, "normal");

  tabla({
    startY: 30,
    columnStyles: { 0: { cellWidth: 34 }, 1: { cellWidth: 54 }, 2: { cellWidth: 36 } },
    body: [
      [etiqueta("Taller:"), taller.nombre, etiqueta("Fecha:"), fechaCorta(contrato.fecha)],
      [etiqueta("Proceso:"), contrato.proceso, etiqueta("Compromiso:"), fechaCorta(contrato.fechaCompromiso)],
      [etiqueta("Corte #:"), String(contrato.corte || ""), etiqueta("Pedido:"), String(contrato.pedido || "")],
      [etiqueta("Cliente:"), contrato.cliente, etiqueta("Status:"), contrato.status],
    ],
  });

  // ---- lo que se mandó a maquilar
  tabla({
    startY: doc.autoTable.previous.finalY + 6,
    head: [["ARTÍCULO", "CANTIDAD", "PRECIO", "IMPORTE"]],
    body: articulos.map((a) => [a.articulo, num(a.cantidad), moneda(a.precio), moneda(a.importe)]),
    foot: [
      [
        { content: "VALOR DEL CONTRATO", styles: { halign: "right" } },
        { content: num(contrato.piezas), styles: { halign: "center" } },
        "",
        derecha(moneda(contrato.valor)),
      ],
    ],
    footStyles: { fillColor: FONDO, textColor: AZUL, fontStyle: "bold" },
    showFoot: "lastPage",
    columnStyles: {
      1: { cellWidth: 28, halign: "center" },
      2: { cellWidth: 30, halign: "right" },
      3: { cellWidth: 36, halign: "right" },
    },
  });

  const obs = dato(contrato.observaciones);
  if (obs) {
    tabla({
      startY: doc.autoTable.previous.finalY + 4,
      body: [[etiqueta("Observaciones:"), obs]],
      columnStyles: { 0: { cellWidth: 34 } },
    });
  }

  // ---- entregas
  const yEntregas = titulo(`ENTREGAS (${entregas.length})`);
  tabla({
    startY: yEntregas,
    head: [["ENTRADA", "FECHA", "PIEZAS", "FALTANTES", "NOTA", "TOTAL", "SALDO"]],
    body: entregas.length
      ? entregas.map((e) => [
          String(e.folio ?? ""),
          fechaCorta(e.fecha),
          num(e.piezas),
          e.faltantes ? num(e.faltantes) : "-",
          e.nota ? String(e.nota.folio) : "Sin nota",
          e.nota ? moneda(e.nota.total) : "-",
          e.nota ? moneda(e.nota.saldo) : "-",
        ])
      : [[{ content: "Todavía no hay entregas de este contrato", colSpan: 7, styles: { halign: "center", textColor: [110, 110, 110] } }]],
    headStyles: { fillColor: FONDO, textColor: AZUL, fontStyle: "bold", halign: "center" },
    columnStyles: {
      0: { halign: "center" },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
      5: { halign: "right" },
      6: { halign: "right", fontStyle: "bold" },
    },
  });

  // ---- anticipos ligados al contrato
  if (anticipos.length) {
    const yAnt = titulo(`ANTICIPOS (${anticipos.length})`);
    tabla({
      startY: yAnt,
      head: [["ANTICIPO", "FECHA", "BANCO", "IMPORTE", "APLICADO", "DISPONIBLE"]],
      body: anticipos.map((a) => [
        String(a.folio ?? ""),
        fechaCorta(a.fecha),
        a.banco,
        moneda(a.importe),
        moneda(a.utilizado),
        moneda(a.saldo),
      ]),
      headStyles: { fillColor: FONDO, textColor: AZUL, fontStyle: "bold", halign: "center" },
      columnStyles: {
        0: { halign: "center" },
        1: { halign: "center" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right", fontStyle: "bold" },
      },
    });
  }

  // ---- resumen: piezas a la izquierda, dinero a la derecha
  let y = doc.autoTable.previous.finalY + 7;
  if (y > pageH - 95) {
    doc.addPage();
    y = 20;
  }
  const mitad = (anchoUtil - 6) / 2;
  const caja = (x, filas) =>
    doc.autoTable({
      startY: y,
      margin: { left: x, right: pageW - x - mitad },
      tableWidth: mitad,
      theme: "grid",
      body: filas.map(([l, v, fuerte]) => [etiqueta(l), derecha(v, fuerte ? { fontStyle: "bold" } : {})]),
      styles: { fontSize: 10, cellPadding: 2.3, lineColor: BORDE, lineWidth: 0.25 },
      columnStyles: { 0: { cellWidth: 44 } },
    });
  caja(ML, [
    ["PIEZAS", num(contrato.piezas)],
    ["RECIBIDAS", num(resumen.recibidas)],
    ...(resumen.faltantes ? [["FALTANTES", num(resumen.faltantes)]] : []),
    ["POR ENTREGAR", num(resumen.pendientes), true],
  ]);
  const finIzq = doc.autoTable.previous.finalY;
  caja(ML + mitad + 6, [
    ["VALOR", moneda(contrato.valor)],
    ["ENTREGADO", moneda(resumen.entregado)],
    ["POR PAGAR", moneda(resumen.porPagar), true],
    ...(resumen.anticipado ? [["ANTICIPO DISPONIBLE", moneda(resumen.anticipoDisponible)]] : []),
  ]);
  const finDer = doc.autoTable.previous.finalY;

  // ---- firmas
  let yFirma = Math.max(finIzq, finDer) + 30;
  if (yFirma > pageH - 26) {
    doc.addPage();
    yFirma = 45;
  }
  const anchoFirma = 68;
  doc.setDrawColor(...AZUL).setLineWidth(0.35);
  doc.line(ML + 8, yFirma, ML + 8 + anchoFirma, yFirma);
  doc.line(pageW - ML - 8 - anchoFirma, yFirma, pageW - ML - 8, yFirma);
  doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...AZUL);
  doc.text("Entregó", ML + 8 + anchoFirma / 2, yFirma + 5.5, { align: "center" });
  doc.text("Recibió (Taller)", pageW - ML - 8 - anchoFirma / 2, yFirma + 5.5, { align: "center" });
  doc.setTextColor(0).setFont(undefined, "normal");
}
